import { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { COGNITO_CLIENT_ID } from '../utils/auth';

const COGNITO_ENDPOINT = 'https://cognito-idp.eu-west-1.amazonaws.com/';
const MFA_ISSUER = 'RepairShop';

async function cognitoRequest(target, body) {
  const response = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`
    },
    body: JSON.stringify(body)
  });
  return response.json();
}

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaSecret, setMfaSecret] = useState(null);
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [session, setSession] = useState(null);
  const [step, setStep] = useState('credentials');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const mfaInputRef = useRef(null);
  const mfaSetupInputRef = useRef(null);

  useEffect(() => {
    if (step === 'mfa' && mfaInputRef.current) {
      mfaInputRef.current.focus();
    }
  }, [step]);

  // As soon as we enter the mfaSetup step, ask Cognito for a fresh TOTP secret
  useEffect(() => {
    if (step === 'mfaSetup' && session && !mfaSecret) {
      (async () => {
        setLoading(true);
        try {
          const data = await cognitoRequest('AssociateSoftwareToken', { Session: session });
          if (data.SecretCode) {
            setMfaSecret(data.SecretCode);
            setSession(data.Session);
            setTimeout(() => mfaSetupInputRef.current && mfaSetupInputRef.current.focus(), 0);
          } else {
            setError('Unable to start MFA setup. Please try signing in again.');
            setStep('credentials');
          }
        } catch (err) {
          setError('Unable to connect. Please try again.');
          setStep('credentials');
        } finally {
          setLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = await cognitoRequest('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: COGNITO_CLIENT_ID,
        AuthParameters: { USERNAME: email, PASSWORD: password }
      });

      if (data.AuthenticationResult) {
        onLogin(data.AuthenticationResult.IdToken, data.AuthenticationResult.RefreshToken);
      } else if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        setSession(data.Session);
        setStep('mfa');
      } else if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        setSession(data.Session);
        setStep('newPassword');
      } else if (data.ChallengeName === 'MFA_SETUP') {
        setSession(data.Session);
        setStep('mfaSetup');
      } else {
        setError('Invalid email or password.');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      const data = await cognitoRequest('RespondToAuthChallenge', {
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: COGNITO_CLIENT_ID,
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
          NEW_PASSWORD: newPassword
        }
      });

      if (data.AuthenticationResult) {
        onLogin(data.AuthenticationResult.IdToken, data.AuthenticationResult.RefreshToken);
      } else if (data.ChallengeName === 'MFA_SETUP') {
        setSession(data.Session);
        setStep('mfaSetup');
      } else if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        setSession(data.Session);
        setStep('mfa');
      } else {
        setError((data.message) || 'Could not set new password. Please try again.');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const verifyData = await cognitoRequest('VerifySoftwareToken', {
        Session: session,
        UserCode: mfaSetupCode
      });

      if (verifyData.Status !== 'SUCCESS') {
        setError('Invalid code. Please check your authenticator app and try again.');
        setLoading(false);
        return;
      }

      const respondData = await cognitoRequest('RespondToAuthChallenge', {
        ChallengeName: 'MFA_SETUP',
        ClientId: COGNITO_CLIENT_ID,
        Session: verifyData.Session,
        ChallengeResponses: { USERNAME: email }
      });

      if (respondData.AuthenticationResult) {
        // Mark this as the preferred MFA method so future logins use the
        // normal SOFTWARE_TOKEN_MFA path instead of MFA_SETUP again
        try {
          await cognitoRequest('SetUserMFAPreference', {
            AccessToken: respondData.AuthenticationResult.AccessToken,
            SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true }
          });
        } catch (prefErr) {
          // Non-critical - the sign-in itself already succeeded
        }
        onLogin(respondData.AuthenticationResult.IdToken, respondData.AuthenticationResult.RefreshToken);
      } else {
        setError('Could not complete MFA setup. Please try signing in again.');
        setStep('credentials');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = await cognitoRequest('RespondToAuthChallenge', {
        ChallengeName: 'SOFTWARE_TOKEN_MFA',
        ClientId: COGNITO_CLIENT_ID,
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
          SOFTWARE_TOKEN_MFA_CODE: mfaCode
        }
      });

      if (data.AuthenticationResult) {
        onLogin(data.AuthenticationResult.IdToken, data.AuthenticationResult.RefreshToken);
      } else {
        setError('Invalid MFA code. Please try again.');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      await cognitoRequest('ForgotPassword', {
        ClientId: COGNITO_CLIENT_ID,
        Username: email
      });
    } catch (err) {
      // Deliberately ignored - see the message below. We don't want a
      // failure here (e.g. the account not existing) to reveal anything
      // different from the success path.
    } finally {
      // Always show the same generic message and move to the code-entry
      // step, whether or not the account actually exists. Cognito's own
      // response can include CodeDeliveryDetails (which would reveal a
      // masked email/phone), so we deliberately don't surface that -
      // showing it either way would let someone probe for valid accounts.
      setMessage('If an account exists for this email, a reset code has been sent.');
      setStep('resetPassword');
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      const data = await cognitoRequest('ConfirmForgotPassword', {
        ClientId: COGNITO_CLIENT_ID,
        Username: email,
        ConfirmationCode: resetCode,
        Password: newPassword
      });

      // ConfirmForgotPassword has no success payload to check for (unlike
      // the other calls in this file, which look for AuthenticationResult
      // etc.) - a Cognito error response is the only thing that has a
      // distinguishing field, so check for that instead.
      if (data.__type) {
        setError(data.message || 'Could not reset password. Please check the code and try again.');
      } else {
        setStep('credentials');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setResetCode('');
        setMessage('Password reset successful. Please sign in with your new password.');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="app-header">
        <h1>Employee Portal</h1>
        <p>Repair Shop Management</p>
      </header>

      <main className="app-main">
        {step === 'credentials' && (
          <form className="lookup-form" onSubmit={handleCredentials}>
            <h2 style={{ marginBottom: '1.2rem', fontSize: '1.1rem' }}>Sign In</h2>
            {message && <p style={{ color: '#27ae60', marginBottom: '1rem', fontSize: '0.9rem' }}>{message}</p>}
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>
            {error && <p style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('forgotPassword'); setError(null); setMessage(null); }}
              style={{ marginTop: '0.5rem', background: 'transparent', color: '#666', border: 'none', textDecoration: 'underline', fontSize: '0.85rem', padding: '0.4rem' }}
            >
              Forgot password?
            </button>
          </form>
        )}

        {step === 'newPassword' && (
          <form className="lookup-form" onSubmit={handleNewPassword}>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Set a New Password</h2>
            <p style={{ marginBottom: '1.2rem', fontSize: '0.9rem', color: '#666' }}>
              This is your first time signing in. Please choose a permanent password.
            </p>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>
            {error && <p style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Set Password'}
            </button>
          </form>
        )}

        {step === 'mfaSetup' && (
          <form className="lookup-form" onSubmit={handleMfaSetupVerify}>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Set Up Two-Factor Authentication</h2>
            <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
              Add this key to your authenticator app (Google Authenticator, Authy, etc.),
              then enter the 6-digit code it generates below.
            </p>
            {mfaSecret ? (
              <div style={{ textAlign: 'center', marginBottom: '1.2rem' }}>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '0.75rem',
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <QRCodeSVG
                    value={`otpauth://totp/${encodeURIComponent(MFA_ISSUER)}:${encodeURIComponent(email)}?secret=${mfaSecret}&issuer=${encodeURIComponent(MFA_ISSUER)}`}
                    size={180}
                  />
                </div>
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.6rem' }}>
                  Can't scan? Enter this key manually:
                  <br />
                  <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{mfaSecret}</span>
                </p>
              </div>
            ) : (
              <p style={{ marginBottom: '1.2rem', fontSize: '0.9rem', color: '#666' }}>
                Generating setup key...
              </p>
            )}
            <div className="form-group">
              <label htmlFor="mfaSetupCode">Authentication Code</label>
              <input
                ref={mfaSetupInputRef}
                id="mfaSetupCode"
                type="text"
                value={mfaSetupCode}
                onChange={(e) => setMfaSetupCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                required
                autoComplete="one-time-code"
                inputMode="numeric"
                disabled={!mfaSecret}
              />
            </div>
            {error && <p style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
            <button type="submit" disabled={loading || !mfaSecret}>
              {loading ? 'Verifying...' : 'Confirm & Sign In'}
            </button>
          </form>
        )}

        {step === 'mfa' && (
          <form className="lookup-form" onSubmit={handleMfa}>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Two-Factor Authentication</h2>
            <p style={{ marginBottom: '1.2rem', fontSize: '0.9rem', color: '#666' }}>
              Enter the code from your authenticator app
            </p>
            <div className="form-group">
              <label htmlFor="mfaCode">Authentication Code</label>
              <input
                ref={mfaInputRef}
                id="mfaCode"
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                required
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </div>
            {error && <p style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('credentials'); setError(null); }}
              style={{ marginTop: '0.5rem', background: 'transparent', color: '#666', border: '1px solid #ddd' }}
            >
              Back
            </button>
          </form>
        )}

        {step === 'forgotPassword' && (
          <form className="lookup-form" onSubmit={handleForgotPasswordRequest}>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Reset Password</h2>
            <p style={{ marginBottom: '1.2rem', fontSize: '0.9rem', color: '#666' }}>
              Enter your email address and we'll send you a code to reset your password.
            </p>
            <div className="form-group">
              <label htmlFor="forgotEmail">Email Address</label>
              <input
                id="forgotEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
              />
            </div>
            {error && <p style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('credentials'); setError(null); setMessage(null); }}
              style={{ marginTop: '0.5rem', background: 'transparent', color: '#666', border: '1px solid #ddd' }}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {step === 'resetPassword' && (
          <form className="lookup-form" onSubmit={handleResetPassword}>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Enter Reset Code</h2>
            {message && <p style={{ color: '#27ae60', marginBottom: '1rem', fontSize: '0.9rem' }}>{message}</p>}
            <div className="form-group">
              <label htmlFor="resetCode">Reset Code</label>
              <input
                id="resetCode"
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="123456"
                required
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </div>
            <div className="form-group">
              <label htmlFor="resetNewPassword">New Password</label>
              <input
                id="resetNewPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="resetConfirmPassword">Confirm Password</label>
              <input
                id="resetConfirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>
            {error && <p style={{ color: '#e74c3c', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('credentials'); setError(null); setMessage(null); }}
              style={{ marginTop: '0.5rem', background: 'transparent', color: '#666', border: '1px solid #ddd' }}
            >
              Back to Sign In
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

export default Login;